/**
 * Privy Expo Auth Provider — React Native implementation of AuthProvider.
 *
 * Uses @privy-io/expo instead of @privy-io/react-auth.
 * Uses ReactNativeSessionManager instead of WebSessionManager.
 *
 * IMPORTANT — Expo/RN consumers must:
 *   1. Install polyfills: fast-text-encoding, react-native-get-random-values
 *   2. Install @privy-io/expo, @privy-io/expo-native-extensions
 *   3. Call ReactNativeSessionManager.configure({ storage, atob }) at startup
 *   4. Call setPlatform({ ... }) from @pooflabs/web at startup
 *   5. Wrap their app tree with PrivyProvider from @privy-io/expo
 *   6. Create this provider and pass the Privy hooks/methods via setPrivyMethods()
 *
 * Unlike the web PrivyWalletProvider which renders a hidden React DOM tree
 * to host Privy's React context, this provider expects the RN app itself
 * to render <PrivyProvider> and then bridge the hooks via setPrivyMethods().
 */
import { ReactNativeSessionManager as SessionManager, createSessionWithPrivy, buildSetDocumentsTransaction, convertRemainingAccounts, } from '@pooflabs/core';
import { setCurrentUser } from '../../global';
import { getPlatform } from '../../platform';
import { PublicKey, Connection, Transaction, VersionedTransaction, } from '@solana/web3.js';
import * as anchor from '@coral-xyz/anchor';
import { SOLANA_DEVNET_RPC_URL, SOLANA_MAINNET_RPC_URL } from '..';
import { confirmAndCheckTransaction } from './transaction-utils';
import bs58 from 'bs58';
const SURFNET_RPC_URL = 'https://surfpool.fly.dev';
// -----------------------------------------------------------------------
// Provider implementation
// -----------------------------------------------------------------------
export class PrivyExpoProvider {
    constructor(appId, networkUrl = null) {
        this.privyMethods = null;
        this.appId = appId;
        this.networkUrl = networkUrl;
    }
    /**
     * Bridge Privy hooks from the app's React tree into this provider.
     *
     * Call this from a component rendered inside <PrivyProvider>:
     * ```tsx
     * const privy = usePrivy();
     * const wallet = useEmbeddedSolanaWallet();
     * const { getAccessToken } = usePrivy();
     * const { getIdentityToken } = useIdentityToken();
     *
     * useEffect(() => {
     *   provider.setPrivyMethods({
     *     isReady: privy.isReady,
     *     isAuthenticated: !!privy.user,
     *     user: privy.user,
     *     login: privy.login,
     *     logout: privy.logout,
     *     getAccessToken,
     *     getIdentityToken,
     *     getWalletProvider: async () => {
     *       if (!wallet.wallets[0]) return null;
     *       const provider = await wallet.wallets[0].getProvider();
     *       return {
     *         address: wallet.wallets[0].address,
     *         signMessage: provider.signMessage,
     *         signTransaction: provider.signTransaction,
     *         signAndSendTransaction: provider.signAndSendTransaction,
     *       };
     *     },
     *   });
     * }, [privy.isReady, privy.user, wallet.wallets]);
     * ```
     */
    setPrivyMethods(methods) {
        this.privyMethods = methods;
    }
    // -------------------------------------------------------------------
    // AuthProvider interface
    // -------------------------------------------------------------------
    async login() {
        var _a;
        await this.ensureReady();
        // Check for existing session — verify the stored address matches the
        // currently authenticated Privy wallet (guards against account switches
        // without an explicit logout).
        const session = await SessionManager.getSession();
        if (session && ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isAuthenticated)) {
            const wallet = await this.privyMethods.getWalletProvider();
            if (wallet && wallet.address === session.address) {
                return { provider: this, address: session.address };
            }
            // Stale session — clear and continue to fresh login flow
            SessionManager.clearSession();
        }
        // Trigger Privy login
        const privyUser = await this.privyMethods.login();
        if (!privyUser)
            return null;
        // Get the wallet
        const wallet = await this.privyMethods.getWalletProvider();
        if (!wallet) {
            throw new Error('No embedded Solana wallet found after login. Ensure embeddedWallets.solana.createOnLogin is configured.');
        }
        // Create Tarobase session
        const accessToken = await this.privyMethods.getAccessToken();
        const identityToken = await this.privyMethods.getIdentityToken();
        if (!accessToken || !identityToken) {
            throw new Error('Failed to get Privy tokens after login.');
        }
        await this.createSession(wallet.address, accessToken, identityToken);
        const user = { provider: this, address: wallet.address };
        setCurrentUser(user);
        return user;
    }
    async runTransaction(_evmTransactionData, solTransactionData, options) {
        var _a;
        await this.ensureReady();
        if (!solTransactionData) {
            throw new Error('Solana transaction data is required');
        }
        const wallet = await this.getWalletOrThrow();
        const rpcUrl = this.getRpcUrl(solTransactionData.network);
        const connection = new Connection(rpcUrl, 'confirmed');
        const remainingAccounts = convertRemainingAccounts(solTransactionData.txArgs[0].remainingAccounts);
        let app_id = solTransactionData.appId;
        if (typeof globalThis !== 'undefined' && globalThis.CUSTOM_TAROBASE_APP_ID_HEADER) {
            app_id = globalThis.CUSTOM_TAROBASE_APP_ID_HEADER;
        }
        if (!app_id) {
            throw new Error('App ID is required');
        }
        // Create a mock wallet adapter for Anchor (only publicKey needed for building TX)
        const walletPublicKey = new PublicKey(wallet.address);
        const anchorWallet = {
            publicKey: walletPublicKey,
            signTransaction: async (tx) => tx,
            signAllTransactions: async (txs) => txs,
        };
        const anchorProvider = new anchor.AnchorProvider(connection, anchorWallet, anchor.AnchorProvider.defaultOptions());
        const finalDeduped = [];
        for (const acc of remainingAccounts) {
            const existing = finalDeduped.find((d) => d.pubkey.equals(acc.pubkey));
            if (existing) {
                existing.isSigner = existing.isSigner || acc.isSigner;
                existing.isWritable = existing.isWritable || acc.isWritable;
            }
            else {
                finalDeduped.push(acc);
            }
        }
        const { tx, blockhash, lastValidBlockHeight } = await buildSetDocumentsTransaction(connection, solTransactionData.txArgs[0].idl, anchorProvider, walletPublicKey, {
            app_id,
            documents: solTransactionData.txArgs[0].setDocumentData,
            delete_paths: solTransactionData.txArgs[0].deletePaths,
            txData: solTransactionData.txArgs[0].txData,
        }, finalDeduped, solTransactionData.lutKey, solTransactionData.preInstructions, false, solTransactionData.additionalLutAddresses);
        // Set blockhash
        const isLegacyTx = 'recentBlockhash' in tx &&
            !('message' in tx && 'staticAccountKeys' in tx.message);
        if (isLegacyTx) {
            tx.recentBlockhash = blockhash;
            tx.lastValidBlockHeight = lastValidBlockHeight;
        }
        else {
            tx.message.recentBlockhash = blockhash;
        }
        try {
            if ((options === null || options === void 0 ? void 0 : options.shouldSubmitTx) === false) {
                // Sign only — don't submit
                const signed = await wallet.signTransaction(tx);
                return {
                    signedTransaction: signed.signedTransaction,
                    blockNumber: 0,
                    gasUsed: '0',
                    data: '',
                };
            }
            // Sign and submit
            const { signature, txInfo } = await this.signAndSubmitInternal(tx, wallet, rpcUrl, connection);
            return {
                transactionSignature: signature,
                blockNumber: (txInfo === null || txInfo === void 0 ? void 0 : txInfo.slot) || 0,
                gasUsed: ((_a = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _a === void 0 ? void 0 : _a.fee.toString()) || '0',
                data: txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta,
            };
        }
        catch (err) {
            console.error(err);
            throw err;
        }
    }
    async signMessage(message) {
        await this.ensureReady();
        const wallet = await this.getWalletOrThrow();
        const messageBytes = getPlatform().textEncode(message);
        const result = await wallet.signMessage(messageBytes);
        return bs58.encode(result.signature);
    }
    async signTransaction(transaction) {
        await this.ensureReady();
        const wallet = await this.getWalletOrThrow();
        // Ensure blockhash is set
        const isLegacyTx = 'recentBlockhash' in transaction &&
            !('message' in transaction && 'staticAccountKeys' in transaction.message);
        if (isLegacyTx) {
            const legacyTx = transaction;
            if (!legacyTx.recentBlockhash) {
                const rpcUrl = this.getRpcUrl();
                const connection = new Connection(rpcUrl, 'confirmed');
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
            }
            if (!legacyTx.feePayer) {
                legacyTx.feePayer = new PublicKey(wallet.address);
            }
        }
        else {
            const versionedTx = transaction;
            if (!versionedTx.message.recentBlockhash) {
                const rpcUrl = this.getRpcUrl();
                const connection = new Connection(rpcUrl, 'confirmed');
                const { blockhash } = await connection.getLatestBlockhash('confirmed');
                versionedTx.message.recentBlockhash = blockhash;
            }
        }
        const result = await wallet.signTransaction(transaction);
        // Deserialize the signed transaction
        const signedBytes = result.signedTransaction;
        try {
            return VersionedTransaction.deserialize(signedBytes);
        }
        catch (_a) {
            return Transaction.from(signedBytes);
        }
    }
    async signAndSubmitTransaction(transaction, feePayer) {
        await this.ensureReady();
        const wallet = await this.getWalletOrThrow();
        const rpcUrl = this.getRpcUrl();
        const connection = new Connection(rpcUrl, 'confirmed');
        // Ensure blockhash
        const isLegacyTx = 'recentBlockhash' in transaction &&
            !('message' in transaction && 'staticAccountKeys' in transaction.message);
        if (isLegacyTx) {
            const legacyTx = transaction;
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
            legacyTx.recentBlockhash = blockhash;
            legacyTx.lastValidBlockHeight = lastValidBlockHeight;
            legacyTx.feePayer = feePayer || new PublicKey(wallet.address);
        }
        else {
            const versionedTx = transaction;
            const { blockhash } = await connection.getLatestBlockhash('confirmed');
            versionedTx.message.recentBlockhash = blockhash;
        }
        const { signature } = await this.signAndSubmitInternal(transaction, wallet, rpcUrl, connection);
        return signature;
    }
    async restoreSession() {
        const session = await SessionManager.getSession();
        if (session) {
            return { provider: this, address: session.address };
        }
        return null;
    }
    async logout() {
        var _a;
        if ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isAuthenticated) {
            await this.privyMethods.logout();
        }
        SessionManager.clearSession();
    }
    async getNativeMethods() {
        return this.privyMethods;
    }
    // -------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------
    async createSession(address, accessToken, idToken) {
        if (!accessToken || !idToken)
            return;
        const result = await createSessionWithPrivy(accessToken, address, idToken);
        if (result) {
            await SessionManager.storeSession(address, result.accessToken, result.idToken, result.refreshToken);
        }
    }
    async getWalletOrThrow() {
        var _a;
        const wallet = await ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.getWalletProvider());
        if (!wallet) {
            throw new Error('No Privy wallet available. Ensure the user is logged in and an embedded Solana wallet exists.');
        }
        return wallet;
    }
    async signAndSubmitInternal(transaction, wallet, rpcUrl, connection) {
        let signature;
        if (rpcUrl.includes('surfpool') || rpcUrl === SURFNET_RPC_URL) {
            // Surfnet: sign locally, then submit raw
            const signed = await wallet.signTransaction(transaction);
            const txSig = await connection.sendRawTransaction(signed.signedTransaction, {
                skipPreflight: true,
                maxRetries: 3,
            });
            signature = txSig;
        }
        else {
            // Non-Surfnet: use Privy's combined sign+send
            const result = await wallet.signAndSendTransaction(transaction, connection);
            signature = result.signature;
        }
        const txInfo = await confirmAndCheckTransaction(connection, signature);
        return { signature, txInfo };
    }
    getRpcUrl(network) {
        if (this.networkUrl)
            return this.networkUrl;
        if (network === 'solana_devnet')
            return SOLANA_DEVNET_RPC_URL;
        if (network === 'solana_mainnet')
            return SOLANA_MAINNET_RPC_URL;
        if (network === 'surfnet')
            return SURFNET_RPC_URL;
        return SOLANA_MAINNET_RPC_URL;
    }
    async ensureReady(timeoutMs = 15000) {
        if (!this.privyMethods) {
            throw new Error('PrivyExpoProvider.setPrivyMethods() must be called before using auth. ' +
                'Bridge the Privy hooks from your React Native component tree.');
        }
        if (this.privyMethods.isReady)
            return;
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const check = () => {
                var _a;
                if ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isReady) {
                    resolve();
                }
                else if (Date.now() - startTime > timeoutMs) {
                    reject(new Error('Privy Expo SDK failed to initialize within timeout.'));
                }
                else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }
}
